import { useState } from 'react'

import {
  useSetResponseFeedbackImpressionMutation,
  useSubmitResponseFeedbackMutation,
} from '~redux/slices/apiSlice'

interface UseResponseFeedbackOptions {
  responseId: string | null
  impression: boolean | null
  feedback?: string | null
  onChange?: (update: {
    impression?: boolean | null
    feedback?: string
    submitter?: string
  }) => void
  /** Whether the written-feedback popovers (submit/clear) are enabled for this surface. */
  enabled?: boolean
}

interface PopoverAnchor {
  el: HTMLElement
  kind: 'submit' | 'clear'
}

/**
 * Shared thumbs up/down + written-feedback logic for MessageBubble and SearchView.
 * `impression`/`feedback` are controlled by the caller (via `onChange`) so it works
 * whether the caller stores them on a message object or in local component state.
 */
export const useResponseFeedback = ({
  responseId,
  impression,
  feedback,
  onChange,
  enabled = false,
}: UseResponseFeedbackOptions): {
  onThumbUpClick: () => void
  onThumbDownClick: (event: React.MouseEvent<HTMLElement>) => void
  submitPopoverProps: { open: boolean; anchorEl: HTMLElement | null; onClose: () => void }
  clearPopoverProps: { open: boolean; anchorEl: HTMLElement | null; onClose: () => void }
  submitFeedback: (feedback: string, submitter?: string) => void
  clearFeedback: () => void
  /** Screen-reader announcement describing the most recent impression change. */
  announcement: string
} => {
  const [setResponseFeedbackImpression] = useSetResponseFeedbackImpressionMutation()
  const [submitResponseFeedback] = useSubmitResponseFeedbackMutation()
  const [popoverAnchor, setPopoverAnchor] = useState<PopoverAnchor | null>(null)
  const [announcement, setAnnouncement] = useState('')

  const announceImpression = (value: boolean | null): void => {
    const text =
      value === true
        ? 'Marked as helpful.'
        : value === false
          ? 'Marked as not helpful.'
          : 'Feedback cleared.'
    // Trailing space toggles so repeated identical announcements are re-read.
    setAnnouncement((prev) => (prev === text ? `${text} ` : text))
  }

  const closePopover = (): void => setPopoverAnchor(null)

  const setImpression = (
    updatedImpression: boolean,
    event?: React.MouseEvent<HTMLElement>,
  ): void => {
    if (!responseId) {
      return
    }

    const finalImpression = updatedImpression === impression ? null : updatedImpression
    onChange?.(
      finalImpression
        ? { impression: finalImpression, feedback: undefined, submitter: undefined }
        : { impression: finalImpression },
    )
    setResponseFeedbackImpression({ response_id: responseId, impression: finalImpression })
    announceImpression(finalImpression)

    if (enabled && finalImpression === false && event) {
      setPopoverAnchor({ el: event.currentTarget, kind: 'submit' })
    }
  }

  const onThumbDownClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (enabled && !!feedback) {
      setPopoverAnchor({ el: event.currentTarget, kind: 'clear' })
      return
    }
    setImpression(false, event)
  }

  const submitFeedback = (feedbackValue: string, submitter?: string): void => {
    if (!responseId) {
      return
    }
    submitResponseFeedback({ response_id: responseId, feedback: feedbackValue, submitter })
    onChange?.({ feedback: feedbackValue, submitter })
    closePopover()
  }

  const clearFeedback = (): void => {
    if (!responseId) {
      return
    }
    onChange?.({ impression: null, feedback: undefined, submitter: undefined })
    setResponseFeedbackImpression({ response_id: responseId, impression: null })
    announceImpression(null)
    closePopover()
  }

  return {
    onThumbUpClick: () => setImpression(true),
    onThumbDownClick,
    submitPopoverProps: {
      open: popoverAnchor?.kind === 'submit',
      anchorEl: popoverAnchor?.kind === 'submit' ? popoverAnchor.el : null,
      onClose: closePopover,
    },
    clearPopoverProps: {
      open: popoverAnchor?.kind === 'clear',
      anchorEl: popoverAnchor?.kind === 'clear' ? popoverAnchor.el : null,
      onClose: closePopover,
    },
    submitFeedback,
    clearFeedback,
    announcement,
  }
}
